import type { StoryboardBoardItem, StoryboardBoardTrackWithItems } from '@/types';
import type { StoryboardVideoResponse } from '@/services/api/storyboard-resources';

type BoardVideoItem = StoryboardBoardItem & {
  title?: string;
  name?: string;
  parentId?: string | null;
  sourceId?: string | null;
  sourceVideoId?: string | null;
  sourceRootVideoId?: string | null;
  sourceOriginId?: string | null;
  editAction?: number | null;
  isActive?: boolean;
  isFinalized?: boolean;
  url?: string | null;
  coverUrl?: string | null;
  status?: number | string | null;
  statusStr?: string | null;
  version?: number | null;
  duration?: number;
  resolution?: string | null;
  frameRate?: number | null;
  fileSize?: number | null;
  format?: string | null;
};

function resolveBoardVideoId(item: StoryboardBoardItem): string | undefined {
  return item.videoId ?? (item.entityType === 'video' ? (item.entityId ?? undefined) : undefined);
}

function resolveBoardVideoLineageRootId(item: BoardVideoItem): string | undefined {
  const videoId = resolveBoardVideoId(item);
  return item.parentId ?? videoId;
}

function resolveStoryboardVideoLineageRootId(
  video: Pick<StoryboardVideoResponse, 'videoId' | 'parentId'>
): string {
  return video.parentId ?? video.videoId;
}

function buildBoardVideoItem(
  track: StoryboardBoardTrackWithItems,
  video: StoryboardVideoResponse,
  previousItem?: BoardVideoItem
): BoardVideoItem {
  const videoDuration =
    typeof video.duration === 'number'
      ? video.duration
      : typeof video.duration === 'string'
        ? Number.parseFloat(video.duration)
        : undefined;
  const startTime = typeof previousItem?.startTime === 'number' ? previousItem.startTime : 0;
  const endTime =
    typeof previousItem?.endTime === 'number'
      ? previousItem.endTime
      : startTime +
        (typeof videoDuration === 'number' && Number.isFinite(videoDuration) ? videoDuration : 3);

  return {
    ...previousItem,
    id: video.videoId,
    trackId: previousItem?.trackId ?? track.id,
    itemType: 'video',
    storyboardId: video.storyboardId,
    videoId: video.videoId,
    entityType: 'video',
    entityId: video.videoId,
    title: video.name ?? previousItem?.title ?? '视频方案',
    name: video.name ?? previousItem?.name,
    parentId: video.parentId ?? null,
    sourceId: video.sourceId ?? null,
    sourceVideoId: video.sourceVideoId ?? null,
    sourceRootVideoId: video.sourceRootVideoId ?? null,
    sourceOriginId: video.sourceOriginId ?? null,
    editAction: video.editAction ?? null,
    url: video.url || previousItem?.url || null,
    coverUrl: video.coverUrl ?? previousItem?.coverUrl ?? null,
    status: video.status,
    statusStr: video.statusStr,
    isActive: true,
    isFinalized: true,
    version: video.version,
    duration:
      typeof videoDuration === 'number' && Number.isFinite(videoDuration)
        ? videoDuration
        : previousItem?.duration,
    resolution: video.resolution ?? null,
    frameRate: video.frameRate ?? null,
    fileSize: video.fileSize ?? null,
    format: video.format ?? null,
    startTime,
    endTime,
    displayOrder: video.displayOrder ?? previousItem?.displayOrder ?? 0,
    createdAt: previousItem?.createdAt ?? video.createdAt,
    updatedAt: new Date().toISOString(),
  };
}

export function syncStoryboardBoardVideoActiveForStoryboard({
  tracks,
  storyboardId,
  video,
  isActive,
}: {
  tracks: StoryboardBoardTrackWithItems[];
  storyboardId: string;
  video: StoryboardVideoResponse;
  isActive: boolean;
}): StoryboardBoardTrackWithItems[] {
  const targetRootId = resolveStoryboardVideoLineageRootId(video);

  return tracks.map((track) => {
    if (track.type !== 'video') {
      return track;
    }

    const targetIndex = track.items.findIndex((item) => {
      const boardVideo = item as BoardVideoItem;
      return (
        boardVideo.storyboardId === storyboardId &&
        resolveBoardVideoLineageRootId(boardVideo) === targetRootId
      );
    });

    if (!isActive) {
      const nextItems = track.items.filter((item) => {
        const boardVideo = item as BoardVideoItem;
        return !(
          boardVideo.storyboardId === storyboardId &&
          resolveBoardVideoId(boardVideo) === video.videoId
        );
      });
      return nextItems.length === track.items.length ? track : { ...track, items: nextItems };
    }

    const nextItem = buildBoardVideoItem(
      track,
      { ...video, isActive: true, isFinalized: true },
      targetIndex >= 0 ? (track.items[targetIndex] as BoardVideoItem) : undefined
    );

    if (targetIndex < 0) {
      return {
        ...track,
        items: [...track.items, nextItem],
      };
    }

    return {
      ...track,
      items: track.items.map((item, index) => (index === targetIndex ? nextItem : item)),
    };
  });
}

export function reorderStoryboardBoardVideoItemsForStoryboard(
  tracks: StoryboardBoardTrackWithItems[],
  storyboardId: string,
  orderedVideoIds: readonly string[]
): StoryboardBoardTrackWithItems[] {
  const orderByVideoId = new Map(orderedVideoIds.map((videoId, index) => [videoId, index]));

  return tracks.map((track) => {
    if (track.type !== 'video') {
      return track;
    }

    const targetItems = track.items.filter((item) => {
      const videoId = resolveBoardVideoId(item);
      return item.storyboardId === storyboardId && videoId != null && orderByVideoId.has(videoId);
    });

    if (targetItems.length === 0) {
      return track;
    }

    const sortedTargetItems = [...targetItems]
      .sort((a, b) => {
        const aOrder = orderByVideoId.get(resolveBoardVideoId(a) ?? '') ?? Number.MAX_SAFE_INTEGER;
        const bOrder = orderByVideoId.get(resolveBoardVideoId(b) ?? '') ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      })
      .map((item) => {
        const displayOrder =
          orderByVideoId.get(resolveBoardVideoId(item) ?? '') ?? item.displayOrder;
        return {
          ...item,
          displayOrder,
        };
      });

    let targetIndex = 0;
    const nextItems = track.items.map((item) => {
      const videoId = resolveBoardVideoId(item);
      if (item.storyboardId !== storyboardId || videoId == null || !orderByVideoId.has(videoId)) {
        return item;
      }
      const nextItem = sortedTargetItems[targetIndex];
      targetIndex += 1;
      return nextItem;
    });

    return {
      ...track,
      items: nextItems,
    };
  });
}
